import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ApiHook } from "../../hooks/apiHook";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { getEpins } from "../../utils/getEpinList";
import { toast } from "react-toastify";
import { BASE_URL } from "../../config/config";
import { reverseNumberDisplay } from "../../utils/currencyNumberDisplay";
import Skeleton from "react-loading-skeleton";
import MyPayPalOrderButton from "../payment/PaypalOrderButton";
import Stripe from "../../views/payment/Stripe";
import BankDetails from "../payment/BankDetails";
import EpinPaymentAuth from "../payment/EpinPaymentAuth";

const EnrollForm = ({
    activeStep,
    setActiveStep,
    handleToggleEnrollModal,
    animateStepTransition,
    data,
    currency,
    placement,
    position,
}) => {
    const {
        register,
        setValue,
        setError,
        trigger,
        watch,
        getValues,
        formState: { errors },

    } = useForm({ defaultValues: { sponsorUsername: data?.sponsorDetails?.username || '' } });

    const [epinValues, setEpinValues] = useState([]);
    const [paymentGateways, setPaymentGateways] = useState(data?.paymentGateways);
    const [addPin, setAddpin] = useState(true);
    const [activeTab, setActiveTab] = useState("");
    const [transPass, setTransPass] = useState("");
    const [transPassCheck, setTransPassCheck] = useState(false);
    const [submitButtonActive, setSubmitButtonActive] = useState(true);
    const [getEwallet, setGetEwallet] = useState(false);
    const [transPassResposne, setTransPassResposne] = useState({
        success: null,
        error: null,
    });
    const [fileResponse, setFileResponse] = useState({
        success: null,
        error: null,
    });
    const [file, setFile] = useState(null);
    const [stripePublicKey, setStripePublicKey] = useState('');
    const [paymentIntentId, setPaymentIntentId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [stripeIntentStatus, setStripeIntentStatus] = useState('');
    const [sponsorUsername, setSponsorUsername] = useState("");

    // ---------------------- watch the values of the Enroll Form --------------------------
    const formValues = watch();
    const moduleStatus = useSelector(
        (state) => state?.dashboard?.appLayout?.moduleStatus
    );
    const regAmount = useSelector(
        (state) => state?.dashboard?.appLayout?.configuration?.regAmount
    );
    const conversionFactor = useSelector(
        (state) => state?.user?.conversionFactor
    );
    const sponsorImage = useSelector(
        (state) => state.dashboard?.appLayout?.user?.image
    );
    const { t } = useTranslation();
    const steps = /* data?.registrationSteps ||  */[
        { step: 1, label: "select_position" },
        { step: 2, label: "pick_your_products" },
        { step: 3, label: "payment" }
    ];

    // -------------------------------------- API ------------------------------------------------
    const userBalance = ApiHook.CallEwalletBalance(getEwallet, setGetEwallet);
    const enrollMutation = ApiHook.CallEnrollUser();
    const stripeIntent = ApiHook.CallCreateStripePaymentIntent();
    const updateStripeIntent = ApiHook.CallUpdateStripePaymentIntent(paymentIntentId);
    const cancelStripeIntent = ApiHook.CallCancelStripePaymentIntent();
    const validSponsor = ApiHook.CallValidEnrollSponsor();
    const Upload = ApiHook.CallBankUpload(
        "register",
        sponsorUsername,
        setSubmitButtonActive,
        setValue,
        setFileResponse
    );
    const deleteBankReciept = ApiHook.CallDeleteBankReceipt(
        setSubmitButtonActive,
        setValue,
        setFileResponse,
        setFile
    );
    //----------------------Api call for field value check-----------------------------------
    const transPassCheckData = ApiHook.CallTransPasswordCheck(
        transPass,
        transPassCheck,
        setTransPassCheck,
        setSubmitButtonActive,
        formValues.totalAmount,
        transPassResposne,
        setTransPassResposne
    );
    const epinList = getEpins(data?.validEpins, conversionFactor, currency);

    //-------------------------- Form Navigation -------------------------
    const handleNext = async () => {
        let isValid = await trigger();

        // Validate sponsor username
        if (activeStep === 1) {
            if (!formValues?.sponsorUsername || formValues?.sponsorUsername.trim() === "") {
                isValid = false;
                setError("sponsorUsername", { message: t("this_field_is_required") });
            } else {
                const isValidSponsor = await validSponsor.mutateAsync({
                    sponsorUsername: formValues?.sponsorUsername,
                });
                if(!isValidSponsor?.status) {
                    isValid = false;
                    setError("sponsorUsername", { message: isValidSponsor?.description || t("invalid_sponsor_username") });
                }
            }
        }

        // Auto select single payment method
        if (data?.paymentGateways?.length === 1) {
            const defaultPayment = data?.paymentGateways[0];
            setActiveTab(defaultPayment.id);
            if (defaultPayment.id === 3) {
                setSubmitButtonActive(false);
            } else if (defaultPayment.id === 2) {
                setGetEwallet(true);
                setSubmitButtonActive(true);
                setValue("transactionPassword", transPass?.transPassword);
            } else {
                setSubmitButtonActive(true);
            }
            setValue("paymentType", String(defaultPayment.id));
        }

        if (isValid) {
            const nextStep = activeStep + 1;
            animateStepTransition(nextStep);
            setActiveStep(nextStep);
        }
    };

    const handleBack = () => {
        setActiveTab('');
        const prevStep = activeStep - 1;
        // Clear uploaded image & value when going back from payment step
        if (activeStep === 3) {
            setValue("bankReceipt", undefined);
            resetEpin();
        }
        if (file) {
            setFile(null);
        }
        animateStepTransition(prevStep);
        setActiveStep(prevStep);
        setSubmitButtonActive(true);
    };

    const handleSubmit = async (paymentId = null, paymentIntent, paymentType, paypalResponse) => {
        if (paymentId === 6) {
            setValue("paymentType", paymentId.toString());
            formValues.paymentType = paymentId.toString();
            formValues.paypalResponse = paypalResponse
        }
        if (paymentId == 5) {
            formValues.stripeResponse = paymentIntent;
        }
        setSubmitButtonActive(true);
        const isValid = await trigger();
        if (transPass?.transPassword) {
            formValues.transactionPassword = transPass.transPassword;
        }
        if (isValid) {
            const payload = {
                position: formValues.position,
                sponsorUsername: formValues.sponsorUsername,
                product: formValues.product,
                totalAmt: formValues.totalAmt,
                pv: formValues.pv,
                totalAmount: formValues.totalAmount,
                regAmount: 0,
                termsAndCondition: formValues.termsAndCondition,
                refundPolicy: formValues.refundPolicy,
                paymentType: formValues.paymentType,
                epins: formValues.epins || [],
                totalEpinAmount: formValues.totalEpinAmount || 0,
            };

            if (formValues.transactionPassword) {
                payload.transactionPassword = formValues.transactionPassword;
            }
            if (formValues.stripeResponse) {
                payload.stripeResponse = formValues.stripeResponse;
            }
            if (formValues.paypalResponse) {
                payload.paypalResponse = formValues.paypalResponse;
            }
            if (formValues.bankReceipt) {
                payload.bankReceipt = formValues.bankReceipt;
            }

            enrollMutation.mutate(payload);
        }
    };

    const handleProductClick = (productId, productName, pv, price) => {
        const totalAmount = Number(price);
        setValue("totalAmt", `${CurrencyConverter(totalAmount, conversionFactor)}`);
        setValue(
            "product",
            { id: productId, name: productName, price: price },
            { shouldValidate: true }
        );
        setValue("pv", pv);
        setValue("totalAmount", totalAmount.toFixed(2));
        setValue("regAmount", 0);
    };

    const resetEpin = () => {
        setEpinValues([]);
        setValue("epins", []);
        setValue("totalEpinAmount", 0);
        setAddpin(true);
    }

    const handlePaymentTabClick = (tabId, title) => {
        if (title !== 'e-pin') {
            resetEpin();
        }
        if (title !== "stripe" && paymentIntentId !== '') {
            const body = {
                stripeStatus: stripeIntentStatus,
                paymentIntentId: paymentIntentId,
            }
            cancelStripeIntent.mutateAsync(body, {
                onSuccess: (res) => {
                    if (res.status === "canceled") {
                        setPaymentIntentId('');
                        setClientSecret('');
                        setStripeIntentStatus('');
                    }
                }
            })
        }
        setActiveTab(tabId);
        if (tabId === 3) {
            setSubmitButtonActive(false);
        } else if (tabId === 2) {
            setGetEwallet(true);
            setSubmitButtonActive(true);
            setValue("transactionPassword", transPass?.transPassword);
        } else if (title === "stripe") {
            setSubmitButtonActive(true);
            setStripePublicKey(data.paymentGateways.find((item) => item.title === "stripe").publicKey);
            if (paymentIntentId === '') {
                const body = {
                    productId: formValues.product.id,
                    type: "register"
                };
                stripeIntent.mutateAsync(body, {
                    onSuccess: (res) => {
                        if (res.status) {
                            setClientSecret(res.client_secret);
                            setPaymentIntentId(res.id);
                            setStripeIntentStatus(res.status)
                        } else {
                            toast.error(res.description)
                        }
                    }
                });
            } else {
                const body = {
                    productId: formValues.product.id,
                    paymentIntentId: paymentIntentId,
                    type: "register"
                };
                updateStripeIntent.mutateAsync(body, {
                    onSuccess: (res) => {
                        setStripeIntentStatus(res.status)
                    }
                })
            }
        } else {
            setSubmitButtonActive(true);
        }
        setValue("paymentType", tabId.toString());
        setValue("totalAmount", formValues?.product?.price?.toFixed(2));
    };

    const handleEpinChange = (epinValues) => {
        let newValues = [];
        let totalEpinAmount = 0;
        setEpinValues(epinValues);
        Object.entries(epinValues)?.map(([key, value]) => {
            totalEpinAmount =
                totalEpinAmount + reverseNumberDisplay(String(value.amount));
            newValues.push(value.value);
        });
        const balance =
            Number(reverseNumberDisplay(String(formValues?.totalAmount))) -
            Number(totalEpinAmount);

        if (balance <= 0) {
            setValue("epinBalance", 0);
            setSubmitButtonActive(false);
            toast.success("Total amount achieved");
        } else {
            setSubmitButtonActive(true);
            setValue("epinBalance", reverseNumberDisplay(String(balance)));
        }
        setValue("epins", newValues);
        setValue("totalEpinAmount", reverseNumberDisplay(String(totalEpinAmount)));
    };

    const removeItemByIndex = (index) => {
        let newBalance = 0;
        const remainingValues = [];
        const updatedEpinValues = [...epinValues];
        const removedItem = updatedEpinValues.splice(index, 1)[0];
        setEpinValues(updatedEpinValues);

        updatedEpinValues.forEach((item) => {
            remainingValues.push(item.value);
        });

        const newTotalEpinAmount =
            Number(reverseNumberDisplay(String(formValues.totalEpinAmount))) -
            Number(reverseNumberDisplay(String(removedItem.amount.toFixed(2))));

        if (newTotalEpinAmount < formValues.totalAmount) {
            newBalance =
                Number(reverseNumberDisplay(String(formValues?.totalAmount))) -
                newTotalEpinAmount;
        }

        setValue(
            "totalEpinAmount",
            reverseNumberDisplay(String(newTotalEpinAmount))
        );
        setValue("epinBalance", reverseNumberDisplay(String(newBalance)) ?? 0);
        setValue("epins", remainingValues);
        if (newBalance <= 0) {
            setSubmitButtonActive(false);
        } else {
            setSubmitButtonActive(true);
        }
    };

    const handleFileChange = (event) => {
        setFileResponse({
            success: null,
            error: null,
        });
        const selectedFile = event.target.files[0];
        setFile(selectedFile);
    };

    const handleUpload = () => {
        const type = "register";
        if (file) {
            Upload.mutate(file, type);
        }
    };

    const handleDeleteBankReciept = () => {
        const data = {
            filepath: formValues?.bankReceipt,
            type: "register",
        };
        if (formValues?.bankReceipt) {
            deleteBankReciept.mutateAsync(data);
        }
    };

    const handleTransPassword = async (item) => {
        const { value } = item;
        setTransPass(value);
        setTransPassResposne({
            success: null,
            error: null,
        });
    };

    const handleSponsorUsernameChange = (e) => {
        const value = e.target.value;
        setSponsorUsername(value);
        setValue("sponsorUsername", value);
        setError("sponsorUsername", { message: "" });
    };

    useEffect(() => {
        setValue("position", String(position));
    }, [position]);

    const filterPaymentGateway = async (price) => {
        const gateways = []
        for await (let item of data?.paymentGateways) {
            if (item.title === "free-joining" && price <= 0) gateways.push(item)
            if (item.title !== "free-joining" && price > 0) gateways.push(item)
        }
        setPaymentGateways(gateways)
    }

    useEffect(() => {
        if (data?.paymentGateways?.length > 0) filterPaymentGateway(formValues?.product?.price)
    }, [formValues?.product]);

    useEffect(() => {
        if (data?.regPack && data?.regPack.length === 1) {
            const { id, name, pairValue, price } = data.regPack[0];
            const totalAmount = Number(price);
            setValue("totalAmt", `${CurrencyConverter(totalAmount, conversionFactor)}`);
            setValue("totalAmount", totalAmount.toFixed(2));
            setValue("product",
                { id: id, name: name, price: price },
                { shouldValidate: true }
            );
            setValue("pv", pairValue);
            setValue("regAmount", 0);
        }
    }, [data?.regPack])

    return (
        <div className="main-content-regsiter">
            <div className="row justify-content-center pt-0 p-4" id="wizardRow">
                <div className="col-md-12 text-center">
                    <div className="wizard-form py-4 my-2">
                        <ul id="progressBar" className="progressbar px-lg-5 px-0">
                            {steps?.map((step, index) => (
                                <li
                                    key={`step-${index + 1}`}
                                    id={`progressList-${index + 1}`}
                                    className={`d-inline-block w-33 position-relative text-center float-start progressbar-list ${activeStep >= step.step ? "active" : ""}`}
                                >
                                    {t('step')}{step.step}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <div id="animation">
                {activeStep === 1 && (
                    <div className="row row_top justify-content-center" id="cardSection">
                        <div className="col-lg-12 col-md-12">
                            <div className="regsiter_step_1_view">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="regsiter_step_1_view_left_sec">
                                            <div className="regsiter_step_1_view_left_sec_head">
                                                {t("sponsor")}
                                            </div>
                                            <div className="regsiter_form_box">
                                                <label htmlFor="sponsorUsername">
                                                    {t("sponsor_username")}
                                                    <span className="text-danger">٭</span>
                                                </label>
                                                <input
                                                    id="sponsorUsername"
                                                    name="sponsorUsername"
                                                    type="text"
                                                    className={`form-control ${errors.sponsorUsername ? "error-field" : ""}`}
                                                    placeholder={t("enter_sponsor_username")}
                                                    {...register("sponsorUsername", {
                                                        required: {
                                                            value: true,
                                                            message: t("this_field_is_required"),
                                                        },
                                                    })}
                                                    onChange={handleSponsorUsernameChange}
                                                    onBlur={async () => await trigger("sponsorUsername")}
                                                />
                                                {errors.sponsorUsername && (
                                                    <span className="error-message-validator">
                                                        {errors.sponsorUsername.message}
                                                    </span>
                                                )}
                                                {/* {
                                                    data?.sponsorDetails && <>
                                                        <div className="regsiter_step_1_view_left_user_bx">
                                                            <div className="regsiter_step_1_view_left_user_bx_image">
                                                                <img
                                                                    src={data?.sponsorDetails?.image ?? "/images/user-profile.png"}
                                                                    alt=""
                                                                />
                                                            </div>
                                                            <div className="regsiter_step_1_view_left_user_bx_txt">
                                                                <strong>{data?.sponsorDetails?.username}</strong>
                                                                <p>
                                                                    {data?.sponsorDetails?.fullName ? (
                                                                        <>
                                                                            {data?.sponsorDetails?.fullName}
                                                                            <br />
                                                                            {data?.sponsorDetails?.email}
                                                                        </>
                                                                    ) : (
                                                                        <Skeleton count={2} />
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </>
                                                } */}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        {moduleStatus?.mlm_plan === "Binary" && (
                                            <div className="regsiter_step_1_view_left_sec" style={{ borderRight: 'none' }}>
                                                <div className="placement_section_reg">
                                                    <strong>{t("position")}</strong>
                                                    {placement && <span>{placement}</span>}
                                                </div>
                                                <div className="regsiter_step_1_view_left_btn_row">
                                                    <label
                                                        className={`regsiter_step_1_view_left_btn ${formValues.position === "L" ? "active" : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="position"
                                                            value={"L"}
                                                            disabled={position === "R"}
                                                            {...register("position", {
                                                                required: true,
                                                            })}
                                                        />
                                                        {t("left")}
                                                    </label>
                                                    <label
                                                        className={`regsiter_step_1_view_left_btn ${formValues.position === "R" ? "active" : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="position"
                                                            value={"R"}
                                                            disabled={position === "L"}
                                                            {...register("position", {
                                                                required: true,
                                                            })}
                                                        />
                                                        {t("right")}
                                                    </label>
                                                </div>
                                                {errors["position"] &&
                                                    formValues.position === "undefined" && (
                                                        <span className="error-message-validator">
                                                            {t("this_field_is_required")}
                                                        </span>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                                onClick={handleNext}
                            >
                                {t("next")}
                            </button>
                        </div>
                    </div>
                )}
                {activeStep === 2 && (
                    <div className="row row_top justify-content-center" id="cardSection">
                        <div className="col-lg-12 col-md-12">
                            <div className="regsiter_step_1_view">
                                <div className="row">
                                    <div className="col-md-12">
                                        {/* Terms and Conditions */}
                                        {/* <div className="row mb-3">
                  <div className="col-md-12">
                    <div className="regsiter_form_box_tandc">
                      <label className="d-flex" htmlFor="termsBox">
                        <input
                          name={"termsAndCondition"}
                          type="checkbox"
                          id="termsBox"
                          style={{ marginRight: "5px" }}
                          {...register("termsAndCondition", {
                            required: true,
                          })}
                        />
                      </label>
                      <a
                        data-bs-toggle="modal"
                        className="pop_terms_btn"
                        onClick={() => handleToggleEnrollModal("termsAndConditions")}
                      >
                        {t("iAcceptTermsAndConditions")}
                        <span className="text-danger"> ٭ </span>
                      </a>
                      {errors["termsAndCondition"] &&
                        formValues["termsAndCondition"] === false && (
                          <span className="error-message-validator">
                            *{t("required")}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="regsiter_form_box_tandc">
                      <label className="d-flex" htmlFor="refundBox">
                        <input
                          name={"refundPolicy"}
                          type="checkbox"
                          id="refundBox"
                          style={{ marginRight: "5px" }}
                          {...register("refundPolicy", {
                            required: true,
                          })}
                        />
                      </label>
                      <a
                        data-bs-toggle="modal"
                        className="pop_terms_btn"
                        onClick={() => handleToggleEnrollModal("refundPolicy")}
                      >
                        {t("refundPolicyAccept")}
                        <span className="text-danger"> ٭ </span>
                      </a>
                      {errors["refundPolicy"] &&
                        formValues["refundPolicy"] === false && (
                          <span className="error-message-validator">
                            *{t("required")}
                          </span>
                        )}
                    </div>
                  </div>
                </div> */}
                                        <div className="right_product_selection_bx">
                                            <div className="regsiter_step_1_view_left_sec_head">
                                                {t("pick_your_products")}
                                            </div>
                                            <div className="right_product_lsting_section">
                                                <div className="row">
                                                    {data?.regPack?.map((product, index) => (
                                                        <div className="col-md-3" key={index}>
                                                            <div
                                                                className={`right_product_box card ${formValues.product?.id === product.id
                                                                    ? "active-card"
                                                                    : ""
                                                                    }`}
                                                                {...register("product", { required: true })}
                                                                onClick={() =>
                                                                    handleProductClick(
                                                                        product.id,
                                                                        product.name,
                                                                        product.pairValue,
                                                                        product.price
                                                                    )
                                                                }
                                                            >
                                                                <div className="right_product_box_image">
                                                                    <img
                                                                        src={
                                                                            product.image ?? "/images/product1.jpg"
                                                                        }
                                                                        alt=""
                                                                    />
                                                                </div>
                                                                <div className="right_product_box__dtails">
                                                                    <div className="right_product_box__head">
                                                                        {product.name}
                                                                        <p className={`product-plan-badge-${product.plan === 1 ? "new" : product.plan === 0 ? "old" : ""}`}>
                                                                            {product.plan === 1 ? "New Plan" : product.plan === 0 ? "Old Plan" : ""}
                                                                        </p>
                                                                    </div>
                                                                    <div className="right_product_box__dsc">
                                                                        <strong>
                                                                            {currency?.symbolLeft}{" "}
                                                                            {CurrencyConverter(
                                                                                product.price,
                                                                                conversionFactor
                                                                            )}
                                                                        </strong>
                                                                        <br />
                                                                        {"PV - "}
                                                                        {product.pairValue}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {data?.regPack === undefined && (
                                                        <div className="col-md-4">
                                                            <div className="right_product_box card">
                                                                <div className="right_product_box_image">
                                                                    <Skeleton width={125} height={125} />
                                                                </div>
                                                                <div className="right_product_box__dtails">
                                                                    <div className="right_product_box__head">
                                                                        <Skeleton width={125} />
                                                                        <Skeleton width={80} count={2} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.product && (
                                                    <span className="error-message-validator">
                                                        {t("this_field_is_required")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-dark text-white float-start back mt-4 rounded-3"
                                onClick={handleBack}
                            >
                                {t("back")}
                            </button>
                            <button
                                type="button"
                                className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                                onClick={handleNext}
                            >
                                {t("next")}
                            </button>
                        </div>
                    </div>
                )}
                {activeStep === 3 && (
                    <div className="row row_top justify-content-center form-business">
                        <div className="col-lg-12 col-md-12" id="payment">
                            <div className="payment_section_tab">
                                <div className="regsiter_step_1_view_left_sec_head">
                                    {t("payment")}
                                    <br />
                                    <strong>
                                        {t("totalAmount")}: {currency.symbolLeft}{" "}
                                        {CurrencyConverter(
                                            formValues.totalAmount,
                                            conversionFactor
                                        )}
                                    </strong>
                                </div>



                                <div className="row">
                                    <div className="col-md-3 pe-0">
                                        <div
                                            className="nav nav-tabs payment_tab_reg"
                                            id="nav-tab"
                                            role="tablist"
                                        >
                                            {paymentGateways?.map((tab) => (
                                                <Fragment key={tab.id}>
                                                    <button
                                                        key={tab.id}
                                                        className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                                        id={`nav-${tab.id}-tab`}
                                                        data-bs-toggle="tab"
                                                        data-bs-target={`#nav-${tab.id}`}
                                                        type="button"
                                                        role="tab"
                                                        aria-controls={`nav-${tab.id}`}
                                                        aria-selected={formValues.paymentType === tab.id}
                                                        {...register("paymentType", { required: true })}
                                                        onClick={() =>
                                                            handlePaymentTabClick(tab.id, tab.title)
                                                        }
                                                    >
                                                        <i className={tab?.icon}></i> {tab.title}
                                                    </button>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-md-9 ps-0">
                                        <div
                                            className="tab-content p-3 border mb-5 payment__tab_cnt"
                                            id="nav-tabContent"
                                        >
                                            {formValues.paymentType === undefined && (
                                                <p>
                                                    <strong>{t("preferredPaymentPlaceholder")}</strong>
                                                </p>
                                            )}
                                            {paymentGateways?.map((tab) => (
                                                <div
                                                    key={tab.id}
                                                    className={`tab-pane fade ${activeTab === tab.id ? "show active" : ""}`}
                                                    id={`nav-${tab.id}`}
                                                    role="tabpanel"
                                                    aria-labelledby={`nav-${tab.id}-tab`}
                                                >
                                                    {tab.title === "e-pin" &&
                                                        (<EpinPaymentAuth addPin={addPin} setAddpin={setAddpin} epinValues={epinValues} setEpinValues={setEpinValues} formValues={formValues} setValue={setValue} setSubmitButtonActive={setSubmitButtonActive} sponsor={data?.sponsorData?.id} />)
                                                    }

                                                    {tab.title === "e-wallet" && (
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group mb-2">
                                                                    <p>{`${t("ewalletBalance")} : ${currency.symbolLeft
                                                                        } ${CurrencyConverter(
                                                                            userBalance?.data?.balanceAmount,
                                                                            conversionFactor
                                                                        )}`}</p>
                                                                    <br />
                                                                    <label htmlFor="transactionPassword">
                                                                        {t("transaction_password")}
                                                                    </label>
                                                                    <input
                                                                        type="password"
                                                                        placeholder=""
                                                                        className="form-control"
                                                                        name="transPassword"
                                                                        onChange={(e) =>
                                                                            handleTransPassword(e.target)
                                                                        }
                                                                    />
                                                                    {transPassResposne?.success && (
                                                                        <div style={{ color: "green" }}>
                                                                            {t(transPassResposne?.success)}
                                                                        </div>
                                                                    )}
                                                                    {transPassResposne?.error && (
                                                                        <div style={{ color: "red" }}>
                                                                            {t(transPassResposne?.error)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-12">
                                                                <button
                                                                    href="#"
                                                                    className="btn btn-sm btn-primary mt-3"
                                                                    onClick={() => setTransPassCheck(true)}
                                                                    disabled={!transPass}
                                                                >
                                                                    {t("apply")}
                                                                </button>
                                                            </div>
                                                            <span className="error-message-validator">
                                                                {transPassCheckData.data?.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {tab.title === "free-joining" && (
                                                        <p>
                                                            <strong>{t("freeJoinPlaceHolder", { type: "enrollment" })}</strong>
                                                        </p>
                                                    )}
                                                    {tab.title === "bank-transfer" && (
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group mb-2">
                                                                    <BankDetails info={data?.bankTransterSetting?.accountInfo} />
                                                                    <label htmlFor="fileUpload">
                                                                        {t("file_upload")}
                                                                    </label>
                                                                    <input
                                                                        id="fileUpload"
                                                                        type="file"
                                                                        placeholder=""
                                                                        className="form-control"
                                                                        name="fileUpload"
                                                                        onChange={handleFileChange}
                                                                    />
                                                                    {fileResponse?.success &&
                                                                        formValues?.bankReceipt !== undefined && (
                                                                            <div style={{ color: "green" }}>
                                                                                {t(fileResponse?.success)}
                                                                            </div>
                                                                        )}
                                                                    {fileResponse?.error && (
                                                                        <div style={{ color: "red" }}>
                                                                            {t(fileResponse?.error)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-12">
                                                                <button
                                                                    href="#"
                                                                    className="btn btn-sm btn-primary mt-3"
                                                                    onClick={handleUpload}
                                                                    disabled={
                                                                        Upload.status === "loading" ||
                                                                        formValues?.bankReceipt ||
                                                                        document.getElementById("fileUpload")
                                                                            ?.value === "" ||
                                                                        file === null
                                                                    }
                                                                >
                                                                    {Upload.status === "loading"
                                                                        ? "Uploading..."
                                                                        : t("upload")}
                                                                </button>
                                                            </div>
                                                            {Upload?.data?.status === true &&
                                                                formValues?.bankReceipt !== undefined && (
                                                                    <>
                                                                        <img
                                                                            src={`${BASE_URL}${Upload?.data?.data?.file?.path}`}
                                                                            alt="receipt"
                                                                            style={{
                                                                                width: "160px",
                                                                                height: "160px",
                                                                                padding: "5px",
                                                                            }}
                                                                        />
                                                                        <button
                                                                            className="recipt_checkout_address_btn"
                                                                            onClick={handleDeleteBankReciept}
                                                                        >
                                                                            <i className="fa fa-xmark"></i>
                                                                        </button>
                                                                        <div style={{ color: "green" }}>
                                                                            {Upload?.data?.message}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            {Upload?.data?.status === false && (
                                                                <span className="error-message-validator">
                                                                    {Upload?.data?.message}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {tab.title === "stripe" && clientSecret !== '' && (
                                                        <p>
                                                            <Stripe clientSecret={clientSecret} handleSubmit={handleSubmit} publicKey={stripePublicKey} />
                                                        </p>
                                                    )}
                                                    {tab.title === "paypal" && formValues.totalAmount > 0 && (
                                                        <MyPayPalOrderButton
                                                            currency={tab?.currency}
                                                            price={formValues.totalAmount}
                                                            handleSubmit={handleSubmit}
                                                            paymentId={tab.id}
                                                            paymentType={tab.title}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {errors.paymentType && (
                                    <span className="error-message-validator">*Required</span>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-dark text-white float-start back rounded-3"
                                disabled={enrollMutation.isLoading ? true : false}
                                onClick={handleBack}
                            >
                                {t("back")}
                            </button>
                            {activeTab !== 5 &&
                                <button
                                    type="submit"
                                    className="btn text-white float-end submit-button rounded-3 bg-color-info"
                                    disabled={submitButtonActive}
                                    onClick={handleSubmit}
                                >
                                    {t("finish")}
                                </button>
                            }
                        </div>
                    </div>
                )}
            </div>
            {enrollMutation.status === "loading" && <div className="reg-submit-loader-overlay">
                <svg xmlns="http://www.w3.org/2000/svg" xlink="http://www.w3.org/1999/xlink" style={{ margin: 'auto', display: 'block', shapeRendering: 'auto' }} width="200px" height="200px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
                    <path fill="none" stroke="#7b33c2" stroke-width="8" stroke-dasharray="42.76482137044271 42.76482137044271" d="M24.3 30C11.4 30 5 43.3 5 50s6.4 20 19.3 20c19.3 0 32.1-40 51.4-40 C88.6 30 95 43.3 95 50s-6.4 20-19.3 20C56.4 70 43.6 30 24.3 30z" stroke-linecap="round" style={{ transform: 'scale(0.8)', transformOrigin: '50px 50px' }}>
                        <animate attributeName="stroke-dashoffset" repeatCount="indefinite" dur="1s" keyTimes="0;1" values="0;256.58892822265625"></animate>
                    </path></svg>
            </div>}
        </div>
    );
};

export default EnrollForm;